import {createApp} from 'vue';
import '../styles.css';
import AppVue from './components/App.vue';
import router from '/src/router';
import {initAuthRedirect} from '/src/api/services/supabase';
import {loadWebMcpRuntime} from '/src/core/webmcp/webmcp-loader';
import {capturePostHogException, initializePostHog} from '/src/posthog';
import {isChunkLoadError, isReloadingForStaleChunk, reloadForStaleChunk} from '/src/core/chunk-recovery';
import {installOffscreenCanvasPolyfill} from '/src/core/offscreen-canvas-polyfill';
import {SENTRY_IGNORED_ERRORS} from '/src/core/sentry-noise';
import {browserPreferredLocale, i18n, setActiveLocale} from '/src/i18n/i18n';
import {resolveActiveLocale} from '/src/i18n/locale-choice';

installOffscreenCanvasPolyfill(window);
const app = createApp(AppVue);
initializePostHog();
app.config.errorHandler = (error, _instance, info) => {
    capturePostHogException(error, {vue_info: info});
    console.error(error);
};
// Registered here rather than on import of the supabase module, so importing that module
// stays free of the router and of a global auth listener.
initAuthRedirect(() => router.currentRoute.value.path);
// A tab opened before a deploy asks for chunks the new deploy no longer has; reload into it.
window.addEventListener('vite:preloadError', (event) => {
    if (isChunkLoadError((event as Event & {payload?: unknown}).payload)) {
        reloadForStaleChunk({storage: sessionStorage, reload: () => window.location.reload(), logger: console});
    }
});
if (!LOCAL_MODE && !import.meta.env.DEV) {
    void import('@sentry/vue').then((Sentry) => {
        Sentry.init({
            app,
            dsn: 'https://c9fc441bbc0203e764dad927b006d5d8@o4507831755014144.ingest.us.sentry.io/4507831757832192',
            integrations: [Sentry.browserTracingIntegration()],
            // Tracing
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [/^https:\/\/lopaka\.app/],
            // Session Replay
            replaysSessionSampleRate: 0.2, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            // Instagram and Facebook in-app browsers inject their own scripts, which fail on their own.
            denyUrls: [/^iabjs:\/\//],
            ignoreErrors: SENTRY_IGNORED_ERRORS,
            beforeSend(event, hint) {
                if (isReloadingForStaleChunk() && isChunkLoadError(hint.originalException)) return null;
                // Quark's page translation rewrites the text nodes Vue owns, so its DOM errors are not ours.
                if (document.querySelector('.quark-translated-font')) return null;
                return event;
            },
        });
    });
}

// Set before the first render so the shell does not flash English; the router
// keeps it in sync on every navigation afterwards.
setActiveLocale(resolveActiveLocale(window.location.pathname, browserPreferredLocale()));
app.use(i18n).use(router).mount('#lopaka_app');
void loadWebMcpRuntime().catch((error) => console.warn('Failed to load WebMCP runtime:', error));
